import React, { useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import "./superRide.css";

/**
 * The pieces every Ride and delivery detail dialog was hand-rolling.
 *
 * Each of TripDetails, CarDetails, CommentDetails and PromotionDetails
 * built its own label/value row out of a nested GridContainer, and three
 * of them had their own copy of the image lightbox wiring. Sharing them
 * here keeps the four dialogs actually consistent rather than
 * approximately so.
 */

export const DetailField = ({ label, value, wide }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={`superRideField ${wide ? "isWide" : ""}`}>
      <span className="superRideLabel">{label}</span>
      <span className="superRideValue">{value}</span>
    </div>
  );
};

export const CopyableField = ({ label, value, wide }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className={`superRideField ${wide ? "isWide" : ""}`}>
      <span className="superRideLabel">{label}</span>
      <span className="superRideValueRow">
        <span className="superRideMono">{value}</span>
        <IconButton size="small" className="superRideCopyButton" onClick={handleCopy} title="Copy">
          {copied ? <CheckIcon fontSize="small" /> : <FileCopyIcon style={{ fontSize: 16 }} />}
        </IconButton>
      </span>
    </div>
  );
};

/**
 * Stars for a 1-5 rating. `value` may be null, in which case nothing is
 * drawn — an unrated trip is not a zero-star trip.
 */
export const Stars = ({ value, showValue = true }) => {
  if (value === null || value === undefined) {
    return <span className="superRideMuted">Not rated</span>;
  }
  const rounded = Math.round(value);
  return (
    <span className="superRideRating">
      <span className="superRideStars">
        {[1, 2, 3, 4, 5].map((star) =>
          star <= rounded ? (
            <StarIcon key={star} />
          ) : (
            <StarBorderIcon key={star} className="isEmpty" />
          )
        )}
      </span>
      {showValue && <span className="superRideRatingValue">{Number(value).toFixed(1)}</span>}
    </span>
  );
};

/**
 * A row of thumbnails that open full size in a new tab.
 *
 * The dialogs this replaces each embedded react-image-lightbox with its
 * own index state — four copies of the same modal-inside-a-modal, which
 * had to fight the dialog for the z-index. A plain link is enough for
 * looking at a vehicle photo or an insurance document.
 */
export const ImageStrip = ({ label, urls, wide }) => {
  const list = (urls || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className={`superRideField ${wide ? "isWide" : ""}`}>
      <span className="superRideLabel">{label}</span>
      <span className="superRideImages">
        {list.map((url, index) => (
          <a
            key={`${url}-${index}`}
            className={`superRideImageButton ${wide ? "superRideImageWide" : ""}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Open full size"
          >
            <img src={url} alt={`${label} ${index + 1}`} />
          </a>
        ))}
      </span>
    </div>
  );
};

export const readableError = (error) => {
  if (!error) return "Something went wrong.";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) return error.graphQLErrors[0].message;
  return error.message || "Something went wrong.";
};

export const humanize = (value) => (value ? String(value).replace(/_/g, " ").toLowerCase() : "");
